import classnames from 'classnames';
import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { DropZone, Text } from '../..';
import { ActionName, BiAddMediaActionTarget, BiAddMediaClickOrigin } from '../../../../constants/Bi';
import { DragType } from '../../../../constants/DragType';
import { useTrackActions } from '../../../../utils/hooks/bi/useTrackActions';
import { ActionClickOrigin } from '../../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import { useTrackAddMediaMediaSourceSelected } from '../../../../utils/hooks/bi/useTrackAddMediaMediaSourceSelected';
import { useIsCurrentSite } from '../../../../utils/hooks/useIsCurrentSite';
import { MediaManagerSDKAnyItem } from '../../../../utils/media-manager-sdk';
import { isNativeFile } from '../../../../utils/media-manager-sdk/items';
import styles from './FolderLabel.scss';
import { IOuter } from './FolderLabel.types';

const accepts = [DragType.Folder, DragType.File];

export const FolderLabel: FC<IOuter> = ({
  item,
  onClick,
  dataHook = 'folder-label',
  dropDisabled,
  onDrop,
  className,
}) => {
  const { trackAddMediaMediaSourceSelectedDragAndDrop } = useTrackAddMediaMediaSourceSelected();
  const { setLastActionParams, trackActionsDone } = useTrackActions();
  const { isCurrentSite } = useIsCurrentSite(item.value);
  const { formatMessage } = useIntl();

  const handleClick = useCallback(() => onClick(item), [onClick, item]);

  const handleOnDrop = useCallback(
    (files: MediaManagerSDKAnyItem[], folder, options) => {
      if (isNativeFile(files[0])) {
        trackAddMediaMediaSourceSelectedDragAndDrop(BiAddMediaActionTarget.BreadcrumbDragAndDrop);
      } else {
        setLastActionParams(files as MediaManagerSDKAnyItem[], { clickOrigin: ActionClickOrigin.BreadcrumbsDragDrop });
        trackActionsDone(files, ActionName.MoveTo);
      }

      onDrop(files, folder, options);
    },
    [onDrop, trackAddMediaMediaSourceSelectedDragAndDrop, setLastActionParams, trackActionsDone],
  );

  return (
    <DropZone
      className={classnames(styles.breadcrumb, className)}
      activeClassName={styles.breadcrumbActive}
      accepts={accepts}
      onDrop={handleOnDrop}
      biOrigin={BiAddMediaClickOrigin.DragAndDrop}
      biTarget={BiAddMediaActionTarget.BreadcrumbDragAndDrop}
      onClick={handleClick}
      item={item.value}
      dataHook={dataHook}
      disabled={dropDisabled}
    >
      <Text weight="thin" size="small" ellipsis>
        {isCurrentSite
          ? formatMessage(
              {
                id: 'files-source.label.current-site-name',
              },
              {
                siteName: item.value.name,
                currentSite: formatMessage({ id: 'files-source.label.current_site' }),
              },
            )
          : item.value.name}
      </Text>
    </DropZone>
  );
};
